/**
 * Turns a session's raw `user_agent` into the "Browser on OS" label the
 * sessions table shows.
 *
 * The core API stores the user agent verbatim, which is the right thing for it
 * to store and the wrong thing to put in front of a person: the row read
 * "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML,
 * like Gecko) Chrome/125.0.0.0 Safari/537.36" where the point of the row is to
 * let someone recognise their own laptop and spot one that is not theirs.
 *
 * Deliberately NOT a user-agent parsing library. This is used for recognition,
 * not for analytics or feature detection: the browser and OS families below
 * are the ones a Duvi platform administrator signs in from, and anything else
 * falls back to a truthful "Unknown browser" rather than to a guess. The order
 * of the checks matters — every Chromium browser also claims to be Chrome and
 * Safari, and iPadOS Safari claims to be a Mac.
 */

const BROWSERS: [name: string, test: RegExp][] = [
  // Chromium family first: each of these also carries "Chrome" and "Safari".
  ["Edge", /\bEdgA?\/|\bEdg\//],
  ["Opera", /\bOPR\/|\bOpera\//],
  ["Samsung Internet", /\bSamsungBrowser\//],
  ["Chrome", /\bChrome\/|\bCriOS\//],
  ["Firefox", /\bFirefox\/|\bFxiOS\//],
  // Safari last: it is what is left once every impersonator is excluded.
  ["Safari", /\bSafari\//],
];

const PLATFORMS: [name: string, test: RegExp][] = [
  // iPadOS reports as a Mac unless "iPad" is checked first.
  ["iOS", /\biPhone\b|\biPad\b|\biPod\b/],
  ["Android", /\bAndroid\b/],
  ["Windows", /\bWindows NT\b|\bWindows\b/],
  ["macOS", /\bMacintosh\b|\bMac OS X\b/],
  ["ChromeOS", /\bCrOS\b/],
  ["Linux", /\bLinux\b|\bX11\b/],
];

function match(pairs: [string, RegExp][], ua: string): string | null {
  for (const [name, test] of pairs) if (test.test(ua)) return name;
  return null;
}

export function duviCoreAdminDescribeUserAgent(userAgent: string): string {
  const ua = (userAgent ?? "").trim();
  if (!ua) return "Unknown device";
  const browser = match(BROWSERS, ua);
  const platform = match(PLATFORMS, ua);
  if (browser && platform) return `${browser} on ${platform}`;
  if (browser) return browser;
  if (platform) return `Unknown browser on ${platform}`;
  return "Unknown browser";
}
